import { formatDateLocal } from "../../../../utils/hooks/dataHandler";
import MainComputationCalculator from "../../../../builders/calculator/mainComputation";
import { getSignedUnutilisedLossAmount } from "../../../MainTaxComputation/Pdf/shared";
import {
  EIS_AMOUNT_FIELDS,
  SIGNED_AMOUNT_FIELDS
} from "./formCSDataFormFields";
import {
  PL_FLIP_DEFAULT,
  convertReferenceAmountToSgd,
  derivePlFigures,
  getNonSgdReferenceValues,
  getReferenceCurrencyMeta,
  sectionSignedTotal
} from "./formCSReferenceUtils";

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const normalizeScheduleType = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, "_")
    .toLowerCase();

const getRowScheduleType = (row = {}) =>
  normalizeScheduleType(
    row.sourceScheduleType ||
      row.schedule?.scheduleType ||
      row.schedule?.type ||
      row.scheduleType ||
      row.type
  );

const isScheduleType = (row, types) => {
  const scheduleType = getRowScheduleType(row);
  return types.some((type) => scheduleType === normalizeScheduleType(type));
};

const isRentalScheduleRow = (row) => isScheduleType(row, ["Rental"]);

const INTEREST_INCOME_SCHEDULE_TYPES = [
  "Interest",
  "Interest_Income",
  "InterestIncome"
];

const isInterestIncomeScheduleRow = (row) =>
  isScheduleType(row, INTEREST_INCOME_SCHEDULE_TYPES);

const sumRows = (rows, { abs = false } = {}) =>
  (rows || []).reduce((sum, row) => {
    if (row.isForfeited) return sum;
    const a = num(row.amount);
    return sum + (abs ? Math.abs(a) : a);
  }, 0);

const sumUnabsorbedBalanceCf = (rows) => {
  if (!Array.isArray(rows)) return null;

  return rows.reduce((sum, row) => {
    if (row?.isForfeited) return sum;
    return sum + num(row?.balanceCf);
  }, 0);
};

const pickUnabsorbedSummaryValue = (summaryValue, fallbackValue) =>
  summaryValue !== null && summaryValue !== undefined
    ? summaryValue
    : (fallbackValue ?? null);

const getLossBfAmount = (row) => getSignedUnutilisedLossAmount(row);

const sumLossBfRows = (rows) =>
  (rows || [])
    .filter((row) => !row.isForfeited)
    .reduce((sum, row) => sum + getLossBfAmount(row), 0);

const sumPlScheduleTypes = (pl, sections, types) => {
  let total = 0;
  sections.forEach((section) => {
    (pl?.[section] || []).forEach((row) => {
      if (isScheduleType(row, types)) {
        total += Math.abs(num(row.amount));
      }
    });
  });
  return total;
};

const sumPlNameKeywords = (pl, sections, keywords) => {
  const lower = keywords.map((k) => k.toLowerCase());
  let total = 0;
  sections.forEach((section) => {
    (pl?.[section] || []).forEach((row) => {
      const name = (row.name || "").toLowerCase();
      if (lower.some((k) => name.includes(k))) {
        total += Math.abs(num(row.amount));
      }
    });
  });
  return total;
};

const filterRefRows = (rows, predicate) => (rows || []).filter(predicate);

const sumRefAddedBack = (rows) =>
  MainComputationCalculator.calTaxAdjustments(
    filterRefRows(rows, (r) => r.taxAdjustment !== "Deduct")
  );
const sumRefDeduct = (rows) =>
  MainComputationCalculator.calTaxAdjustments(
    filterRefRows(rows, (r) => r.taxAdjustment === "Deduct")
  );
const sumRef = (rows) =>
  Math.abs(MainComputationCalculator.calTaxAdjustments(rows));

const sumTaxAdjustmentSection = (rows) =>
  MainComputationCalculator.calTaxAdjustments(rows || []);

const sumOtherIncomeSection = (rows) =>
  MainComputationCalculator.justArraySum(rows || []);

const DATE_AUTO_FILL_KEYS = new Set([
  "basisPeriodFrom",
  "basisPeriodTo",
  "firstFYE",
  "prevFYE"
]);

const EXCLUDED_AUTO_FILL_KEYS = new Set([
  "adjPLBefDed",
  "sgAdjPLAft",
  "sgRent",
  "totSgFI",
  "ci",
  "sholderChange",
  "uCALDChangePrinAct",
  "fullTxX",
  "ptisDonInd",
  "foreignSourceSaleNotTax"
]);

const NON_SGD_SECTION_BY_FIELD = {
  profitLossBeforeTaxation: "net_profit_before_tax",
  c1_NTDeductibleExp: "non_tax_deductible_expenses",
  receiptNotTxAmt: "non_taxable_income",
  sepSrcIncome: "separate_source_income_expenses",
  c1_BC: "balancing_charge",
  renoWorksExpS14Q: "section_14n",
  c1_FurtherDed: "further_deductions",
  sgAdjPLAft: "adjusted_profit_before_capital_allowances",
  unutilCABFNorm: "unutilised_ca_bf",
  cyCANorm: "current_year_ca",
  totSgFI: "total_before_donations",
  unutilDonationBFNorm: "unutilised_donations_bf",
  cyDonation: "current_year_donations"
};

const isRenovationRow = (row) => {
  const title = (row.name || row.schedule || "").toLowerCase();
  const sch = String(row.schedule || "").toLowerCase();
  return (
    title.includes("renovation") ||
    title.includes("refurbishment") ||
    title.includes("s14n") ||
    title.includes("s14q")
  );
};

const formatDateRef = (iso) => {
  if (!iso) return null;
  try {
    return formatDateLocal(new Date(iso));
  } catch {
    return String(iso).slice(0, 10);
  }
};

/** @typedef {{ key: string, label: string, value: number|string|null, hint?: string }} FormCSRefField */

/** @typedef {{ id: string, title: string, fields: FormCSRefField[] }} FormCSRefSection */

/**
 * Build per-field reference hints for dataFormCS (modules C–J) from Main TC + P/L.
 * @returns {FormCSRefSection[]|null}
 */
export const buildFormCSFieldReferenceSections = (
  parsedMainComp,
  plSnapshot,
  selectedTc
) => {
  if (!parsedMainComp && !plSnapshot) return null;

  const company = parsedMainComp?.company || {};
  const isInvestmentCompany = company.companyType === "investment";
  const cs = parsedMainComp?.computedSummary || {};
  const ref = parsedMainComp?.referenceData || {};
  const unabs = parsedMainComp?.unabsorbedSummary || {};
  const plf = derivePlFigures(plSnapshot);

  const fyeStart =
    company.financialYearStart ||
    plSnapshot?.financialYearStart ||
    selectedTc?.financialYearStart;
  const fyeEnd =
    company.financialYearEnd ||
    plSnapshot?.financialYearEnd ||
    selectedTc?.financialYearEnd;

  const flip = plSnapshot?.flipSignMode || PL_FLIP_DEFAULT;
  const plRevenue = plSnapshot
    ? sectionSignedTotal(plSnapshot.Revenue, flip, "Revenue")
    : null;
  const plCos = plSnapshot
    ? sectionSignedTotal(plSnapshot.CostOfSales, flip, "Cost of Sales")
    : null;
  const grossPl = plRevenue != null && plCos != null ? plRevenue + plCos : null;

  const taxAdj = ref.taxAdjustments || [];
  const separateSourceTaxAdj = ref.taxAdjustmentSeparateSourcedIncome || [];
  const balancingChargeTaxAdj = ref.taxAdjustmentBalancingCharge || [];
  const legacyFurtherDed = ref.furtherDeductions || [];
  const s14nFurtherDed = ref.furtherDeductionS14n || [];
  const otherFurtherDed = ref.furtherDeductionOther || [];
  const otherSrc = ref.otherSourceIncome || [];
  const cyCa = ref.currentYearCapitalAllowances || [];
  const cyDon = ref.currentYearDonations || [];

  const renoDed = s14nFurtherDed.length
    ? s14nFurtherDed
    : legacyFurtherDed.filter(isRenovationRow);
  const otherFurther = otherFurtherDed.length
    ? otherFurtherDed
    : legacyFurtherDed.filter((r) => !isRenovationRow(r));

  const rentalPl = sumPlScheduleTypes(plSnapshot, ["OtherIncome"], ["Rental"]);
  const interestPl = sumPlScheduleTypes(
    plSnapshot,
    ["OtherIncome", "Revenue"],
    INTEREST_INCOME_SCHEDULE_TYPES
  );
  const interestRef = sumOtherIncomeSection(
    filterRefRows(otherSrc, isInterestIncomeScheduleRow)
  );

  const rentalRefTotal = sumOtherIncomeSection(
    filterRefRows(otherSrc, isRentalScheduleRow)
  );

  const medicalPl = sumPlScheduleTypes(
    plSnapshot,
    ["Expenses", "Revenue"],
    ["Medical Expense"]
  );
  const transportPl = sumPlScheduleTypes(
    plSnapshot,
    ["Expenses"],
    ["Movement", "Transport"]
  );
  const entertainPl = sumPlNameKeywords(
    plSnapshot,
    ["Expenses"],
    ["entertain"]
  );
  const directorPl = sumPlNameKeywords(
    plSnapshot,
    ["Expenses"],
    ["director", "remuneration"]
  );

  const foreignPl = sumPlScheduleTypes(
    plSnapshot,
    ["Revenue", "OtherIncome"],
    ["Foreign Trade"]
  );

  const unutilCaBf = sumRows(parsedMainComp?.unutilisedCapitalAllowances, {
    abs: true
  });
  const unutilLossBf = Math.abs(
    sumLossBfRows(parsedMainComp?.unutilisedLosses)
  );
  const unutilDonBf = sumRows(parsedMainComp?.unutilisedDonations, {
    abs: true
  });

  const caCf = sumUnabsorbedBalanceCf(unabs.caRows);
  const lossCf = sumUnabsorbedBalanceCf(unabs.lossRows);
  const donationCf = sumUnabsorbedBalanceCf(unabs.donationRows);

  const cyCaAmt = Math.abs(sumTaxAdjustmentSection(cyCa));
  const cyDonationAmt = Math.abs(sumTaxAdjustmentSection(cyDon));
  const hasUnutilisedLossBf = (parsedMainComp?.unutilisedLosses || []).some(
    (row) => !row?.isForfeited && getLossBfAmount(row) > 0
  );
  const hasBfClaim = unutilCaBf > 0 || hasUnutilisedLossBf || unutilDonBf > 0;

  const currencyMeta = getReferenceCurrencyMeta(
    parsedMainComp,
    plSnapshot,
    selectedTc
  );
  const nonSgdReference = getNonSgdReferenceValues(
    parsedMainComp,
    currencyMeta
  );

  const sections = [
    {
      id: "module-c",
      title: "Basis period & FS",
      fields: [
        {
          key: "basisPeriodFrom",
          label: "Basis period from",
          value: formatDateRef(fyeStart),
          hint: "Company / TC financial year start"
        },
        {
          key: "basisPeriodTo",
          label: "Basis period to",
          value: formatDateRef(fyeEnd),
          hint: "Company / TC financial year end"
        },
        {
          key: "firstFYE",
          label: "First set — FYE",
          value: formatDateRef(fyeEnd)
        }
      ]
    },
    ...(isInvestmentCompany
      ? []
      : [
          {
            id: "module-f",
            title: "P/L supplement",
            fields: [
              {
                key: "totalRevenue",
                label: "Total revenue",
                value: plRevenue ?? plf?.revenueDisplay ?? null,
                hint: "P/L revenue (excluding other income)"
              },
              {
                key: "grossPL",
                label: "Gross profit / loss",
                value: grossPl,
                hint: "P/L revenue plus cost of sales"
              }
            ]
          }
        ]),
    {
      id: "module-d",
      title: "Tax adjustments",
      fields: [
        {
          key: "profitLossBeforeTaxation",
          label: "Profit/loss before taxation",
          value:
            parsedMainComp?.netProfitBeforeTax ??
            plSnapshot?.netProfitBeforeTax ??
            plf?.npbt ??
            null
        },
        {
          key: "c1_NTDeductibleExp",
          label: "Non-deductible items",
          value: sumRefAddedBack(taxAdj) || null,
          hint: "Main TC tax adjustments (added back)"
        },
        {
          key: "receiptNotTxAmt",
          label: "Non-taxable items",
          value: sumRefDeduct(taxAdj) || null,
          hint: "Main TC tax adjustments (deduct)"
        },
        {
          key: "sepSrcIncome",
          label: "Other sourced income / (expenses)",
          value: sumTaxAdjustmentSection(separateSourceTaxAdj) || null,
          hint: "Main TC tax adjustment - separate sourced income"
        },
        {
          key: "c1_BC",
          label: "Balancing charge",
          value: sumRef(balancingChargeTaxAdj) || null,
          hint: "Main TC tax adjustment - balancing charge"
        },
        {
          key: "renoWorksExpS14Q",
          label: "Further Deduction under S14N",
          value: sumRef(renoDed) || null,
          hint: "Further deduction S14N"
        },
        {
          key: "c1_FurtherDed",
          label: "Other Further Deduction",
          value: sumRef(otherFurther) || null,
          hint: "Other further deduction"
        },
        {
          key: "sgAdjPLAft",
          label: "Adjusted Profit Before Capital Allowances",
          value: cs.adjustedProfitAfterTaxAdjustments ?? null
        }
      ]
    },
    {
      id: "module-e",
      title: "Brought forward & donations",
      fields: [
        {
          key: "unutilCABFNorm",
          label: "Unutilised CA b/f",
          value: unutilCaBf || null
        },
        {
          key: "cyCANorm",
          label: "Current year CA",
          value: cyCaAmt || null
        },
        {
          key: "unutilLossBFNorm",
          label: "Unutilised losses b/f",
          value: unutilLossBf || null
        },
        {
          key: "sgOtherI",
          label: "Other taxable income",
          value:
            sumOtherIncomeSection(
              filterRefRows(
                otherSrc,
                (r) =>
                  !isRentalScheduleRow(r) && !isInterestIncomeScheduleRow(r)
              )
            ) || null
        },
        {
          key: "totSgFI",
          label: "Total income/loss before donations",
          value: cs.adjustedProfitAfterLosses ?? null
        },
        {
          key: "unutilDonationBFNorm",
          label: "Unutilised donations b/f",
          value: unutilDonBf || null
        },
        {
          key: "cyDonation",
          label: "Current year donations",
          value: cyDonationAmt || null
        },
        {
          key: "ci",
          label: "Chargeable income after donations",
          value:
            cs.chargebleIncomeBeforeExemptAmount ??
            cs.adjustedProfitAfterDonations ??
            null
        },
        {
          key: "unutilCACFNorm",
          label: "Unutilised CA c/f",
          value: pickUnabsorbedSummaryValue(caCf, cs.unutilisedCAcf),
          hint: "Main Computation unabsorbed summary - Capital Allowances"
        },
        {
          key: "unutilLossCFNorm",
          label: "Unutilised losses c/f",
          value: pickUnabsorbedSummaryValue(lossCf, cs.unutilisedLosscf),
          hint: "Main Computation unabsorbed summary - Losses"
        },
        {
          key: "unutilDonationCFNorm",
          label: "Unutilised donations c/f",
          value: pickUnabsorbedSummaryValue(
            donationCf,
            cs.unutilisedDonationcf
          ),
          hint: "Main Computation unabsorbed summary - Donations"
        }
      ]
    }
    // {
    //   id: "module-g",
    //   title: "Qualification & changes",
    //   fields: [
    //     {
    //       key: "sholderChange",
    //       label: "Substantial shareholder change",
    //       value: hasBfClaim ? "Required if b/f > 0" : null,
    //       hint: hasBfClaim
    //         ? "Complete when CA / loss / donation b/f claimed"
    //         : undefined
    //     },
    //     {
    //       key: "uCALDChangePrinAct",
    //       label: "Change in principal activities",
    //       value: null,
    //       hint: "Manual"
    //     },
    //     {
    //       key: "unutilCALDClaimS23S37",
    //       label: "Unutilised CA/LD claim (S23/S37)",
    //       value: null,
    //       hint: "Manual — exemption utilisation"
    //     },
    //     {
    //       key: "fullTxX",
    //       label: "Start-up tax exemption",
    //       value: parsedMainComp?.isStartup ? "Check eligibility" : "No / NA",
    //       hint: parsedMainComp?.isStartup
    //         ? "Tax Comp flagged as startup"
    //         : "Not flagged as startup in Tax Comp"
    //     }
    //   ]
    // },
    // {
    //   id: "module-h",
    //   title: "6. R&D",
    //   fields: [
    //     {
    //       key: "expRD",
    //       label: "R&D deduction (14C)",
    //       value: null,
    //       hint: "Not in Main TC snapshot — use R&D schedules"
    //     },
    //     {
    //       key: "expRDSG",
    //       label: "R&D in Singapore (14C)",
    //       value: null
    //     },
    //     {
    //       key: "enhanceDeductRD",
    //       label: "Enhanced deduction (14D)",
    //       value: null
    //     }
    //   ]
    // },
    // {
    //   id: "module-i",
    //   title: "7. EIS",
    //   fields: [
    //     {
    //       key: "appStockConvAsset",
    //       label: "Stock / capital asset conversion",
    //       value: null,
    //       hint: "Manual — EIS eligibility"
    //     },
    //     {
    //       key: "c1_EnhancedEISDed",
    //       label: "Total EIS enhanced deduction",
    //       value: null
    //     },
    //     {
    //       key: "eis_ClaimCashPayout",
    //       label: "Claim EIS cash payout",
    //       value: null,
    //       hint: "Manual"
    //     },
    //     {
    //       key: "eis_ClaimDedAll",
    //       label: "Claim EIS enhanced deduction",
    //       value: null,
    //       hint: "Manual"
    //     },
    //     ...EIS_AMOUNT_FIELDS.map(({ key, label }) => ({
    //       key,
    //       label,
    //       value: null,
    //       hint: "When EIS claim selected"
    //     }))
    //   ]
    // },
    // {
    //   id: "module-j",
    //   title: "PTIS & foreign assets",
    //   fields: [
    //     {
    //       key: "ptisDonInd",
    //       label: "PTIS overseas donor",
    //       value:
    //         cyDonationAmt > 0 ? "Required if Current Year Donation > 0" : "NA",
    //       hint:
    //         cyDonationAmt > 0
    //           ? `Current Year Donation ref: ${cyDonationAmt}`
    //           : undefined
    //     },
    //     {
    //       key: "foreignAssetsSaleGainLoss",
    //       label: "Foreign asset disposal gain/loss",
    //       value: foreignPl || null,
    //       hint: "Foreign trade / branch P&L lines"
    //     }
    //     {
    //       key: "foreignSourceSaleGainsRemit",
    //       label: "Foreign gains remitted",
    //       value: null,
    //       hint: "Manual — remittance details"
    //     },
    //     {
    //       key: "foreignSourceSaleNotTax",
    //       label: "Section 10L(8) not taxable",
    //       value: null,
    //       hint: "Required when foreign disposal amounts ≠ 0"
    //     }
    //   ]
    // }
  ];

  if (!currencyMeta.isForeignCurrency) return sections;

  return sections.map((section) => ({
    ...section,
    fields: (section.fields || []).map((field) => {
      if (typeof field.value !== "number") return field;

      const sectionValue =
        nonSgdReference?.sectionValues[NON_SGD_SECTION_BY_FIELD[field.key]];
      const sgdValue =
        field.key === "ci"
          ? nonSgdReference?.taxSummary.chargeableIncomeBeforeExemptAmount
          : sectionValue;

      return {
        ...field,
        sgdValue:
          typeof sgdValue === "number"
            ? SIGNED_AMOUNT_FIELDS.has(field.key)
              ? sgdValue
              : Math.abs(sgdValue)
            : convertReferenceAmountToSgd(field.value, currencyMeta),
        sourceCurrency: currencyMeta.currency,
        sourceCurrencyRate: currencyMeta.currencyRate
      };
    })
  }));
};

export const buildFormCSAutoFillValues = (
  parsedMainComp,
  plSnapshot,
  selectedTc
) => {
  const sections = buildFormCSFieldReferenceSections(
    parsedMainComp,
    plSnapshot,
    selectedTc
  );
  if (!sections?.length) return {};

  return sections.reduce((acc, section) => {
    (section.fields || []).forEach((field) => {
      if (!field?.key || EXCLUDED_AUTO_FILL_KEYS.has(field.key)) return;
      if (field.value == null || field.value === "") return;

      if (typeof field.value === "number") {
        const amount =
          typeof field.sgdValue === "number" ? field.sgdValue : field.value;
        if (SIGNED_AMOUNT_FIELDS.has(field.key)) {
          acc[field.key] = amount;
        } else {
          acc[field.key] = Math.abs(amount);
        }
        return;
      }

      if (DATE_AUTO_FILL_KEYS.has(field.key)) {
        acc[field.key] = String(field.value);
      }
    });
    return acc;
  }, {});
};
